import { TrendingUp, TrendingDown, Calendar, DollarSign } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function PnLTab({ 
  pnlData, 
  pnlPeriod, 
  setPnlPeriod, 
  pnlDate, 
  setPnlDate,
  projectedPnl 
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-stone-900">Estado de Resultados (P&L)</h1>
        <div className="flex gap-2">
          <Input
            type="date"
            value={pnlDate}
            onChange={(e) => setPnlDate(e.target.value)}
            className="w-40"
          />
          <Select value={pnlPeriod} onValueChange={setPnlPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Diario</SelectItem>
              <SelectItem value="weekly">Semanal</SelectItem>
              <SelectItem value="monthly">Mensual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {pnlData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-6 border border-stone-200">
              <p className="text-sm text-stone-500">Ingresos</p>
              <p className="text-2xl font-bold text-emerald-700">${pnlData.revenue?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-stone-200">
              <p className="text-sm text-stone-500">Costo de Ventas</p>
              <p className="text-2xl font-bold text-orange-600">${pnlData.cogs?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-stone-200">
              <p className="text-sm text-stone-500">Utilidad Bruta</p>
              <p className="text-2xl font-bold text-blue-700">${pnlData.gross_profit?.toLocaleString()}</p>
              <p className="text-sm text-stone-400">{pnlData.gross_margin?.toFixed(1)}% margen</p>
            </div>
            <div className={`rounded-2xl p-6 border ${pnlData.net_profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm ${pnlData.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Utilidad Neta</p>
              <p className={`text-2xl font-bold ${pnlData.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                ${pnlData.net_profit?.toLocaleString()}
              </p>
              <p className={`text-sm ${pnlData.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {pnlData.net_margin?.toFixed(1)}% margen
              </p>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Income Statement */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-700" />
                Estado de Resultados
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-stone-600">Ingresos por Ventas</span>
                  <span className="font-semibold text-emerald-700">+${pnlData.revenue?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-stone-600">(-) Costo de Mercancía Vendida</span>
                  <span className="font-semibold text-red-600">-${pnlData.cogs?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b bg-stone-50 px-2 rounded">
                  <span className="font-semibold text-stone-700">= Utilidad Bruta</span>
                  <span className="font-bold text-blue-700">${pnlData.gross_profit?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-stone-600">(-) Gastos Operativos</span>
                  <span className="font-semibold text-orange-600">-${pnlData.total_expenses?.toLocaleString()}</span>
                </div>
                <div className={`flex justify-between py-2 px-2 rounded ${pnlData.net_profit >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <span className="font-semibold text-stone-700">= Utilidad Neta</span>
                  <span className={`font-bold ${pnlData.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    ${pnlData.net_profit?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Expenses by Category */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Gastos por Categoría</h3>
              <div className="space-y-3">
                {pnlData.expenses_by_category && Object.entries(pnlData.expenses_by_category).map(([category, amount]) => (
                  <div key={category} className="flex justify-between items-center">
                    <span className="text-stone-600 capitalize">{category}</span>
                    <span className="font-semibold text-stone-900">${amount?.toLocaleString()}</span>
                  </div>
                ))}
                {(!pnlData.expenses_by_category || Object.keys(pnlData.expenses_by_category).length === 0) && (
                  <p className="text-sm text-stone-400 text-center py-4">Sin gastos registrados</p>
                )}
              </div>
            </div>
          </div>

          {/* Top Products P&L */}
          {pnlData.product_breakdown?.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Rentabilidad por Producto</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm text-stone-600">Producto</th>
                      <th className="px-4 py-2 text-left text-sm text-stone-600">Cantidad</th>
                      <th className="px-4 py-2 text-left text-sm text-stone-600">Ingresos</th>
                      <th className="px-4 py-2 text-left text-sm text-stone-600">Costo</th>
                      <th className="px-4 py-2 text-left text-sm text-stone-600">Utilidad</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {pnlData.product_breakdown.slice(0, 10).map((product, idx) => (
                      <tr key={idx} className="hover:bg-stone-50">
                        <td className="px-4 py-2 font-medium text-stone-900">{product.name}</td>
                        <td className="px-4 py-2 text-stone-600">{product.quantity}</td>
                        <td className="px-4 py-2 text-emerald-700">${product.revenue?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-orange-600">${product.cost?.toLocaleString()}</td>
                        <td className={`px-4 py-2 font-semibold ${product.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          ${product.profit?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Projections */}
      {projectedPnl && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 text-white">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Proyección 6 Meses
          </h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {projectedPnl.projections?.map((month, idx) => (
              <div key={idx} className="bg-white/10 rounded-xl p-4">
                <p className="text-blue-200 text-xs">{month.month}</p>
                <p className="font-bold text-lg">${(month.revenue / 1000).toFixed(0)}k</p>
                <p className={`text-sm ${month.net_profit >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                  {month.margin?.toFixed(1)}% margen
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
